import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, Quote, Video } from 'lucide-react';
import { team, promoVideoUrl, aboutHeroImage } from '../data/mock';
import CTA from '../components/CTA';
import Gallery from '../components/Gallery';

export default function About() {
  return (
    <main>
      <section className="pt-8 md:pt-12 pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-[2rem] overflow-hidden shadow-xl h-[260px] sm:h-[360px] lg:h-[460px]">
            <img
              src={aboutHeroImage}
              alt="Coach Abrie smiling with a toddler in the Little Splash indoor pool"
              fetchpriority="high"
              decoding="async"
              data-testid="about-hero-image"
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 28%' }}
            />
          </div>
          <div className="mt-10 max-w-3xl">
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">About Us</div>
            <h1 className="font-display font-bold text-4xl md:text-6xl text-splash-green leading-tight">Where confidence begins.</h1>
            <p className="mt-6 text-splash-green/80 text-lg leading-relaxed">
              Little Splash Swim was created to provide children with a calm, supportive environment where they can build confidence, comfort, and water safety skills at their own pace. Our small-group approach allows for individualized attention while helping swimmers feel safe, encouraged, and successful.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-splash-creamDark">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Meet Our Team</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-splash-green">Certified. Caring. Consistent.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {team.map((m) => (
              <div key={m.name} className="bg-white rounded-3xl p-8 border border-splash-sand text-center card-hover">
                <div className="w-40 h-40 mx-auto rounded-full overflow-hidden ring-4 ring-splash-teal/20">
                  <img src={m.image} alt={m.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                </div>
                <h3 className="font-display font-bold text-2xl text-splash-green mt-6">{m.name}</h3>
                <div className="text-splash-coralDark text-sm font-semibold mt-1">{m.role}</div>
                <div className="text-splash-green/70 text-sm mt-3">{m.credentials}</div>
                {m.email && (
                  <a href={`mailto:${m.email}`} className="inline-flex items-center gap-2 mt-5 text-splash-teal font-semibold hover:text-splash-tealDark">
                    <Mail className="w-4 h-4" /> Email {m.name.split(' ')[1]}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">The Heart Behind Little Splash</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-splash-green leading-tight">A calmer way to learn to swim.</h2>
            <p className="mt-6 text-splash-green/80 leading-relaxed">
              Little Splash Swim was born from my years as a caregiver and instructor, noticing how big, busy pools can overwhelm young or nervous swimmers.
            </p>
            <p className="mt-4 text-splash-green/80 leading-relaxed">
              I envisioned a more personal, calm space where children could find their confidence through patient guidance. Our program centers on small classes and a quiet environment where every child feels safe and successful. As founder and lead instructor, I am dedicated to teaching essential safety and foundational skills while building lasting trust and a true love for the water.
            </p>
            <Link to="/programs" className="inline-flex items-center gap-2 mt-8 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-6 py-3 font-semibold btn-transition">
              About Our Classes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative">
            <div className="bg-splash-teal/10 border border-splash-sand rounded-3xl p-10">
              <Quote className="w-10 h-10 text-splash-coral" />
              <p className="mt-4 font-display text-2xl md:text-3xl leading-snug text-splash-green">
                “The best gift you can give your child is the ability to save their own life.”
              </p>
              <div className="mt-6 text-splash-green/70 text-sm">— Coach Abrie, Founder</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-splash-cream" data-testid="about-video-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 uppercase tracking-widest text-splash-coral text-xs font-semibold">
              <Video className="w-3.5 h-3.5" /> Inside a Real Lesson
            </div>
            <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-splash-green">See our calm, quiet pool at work.</h2>
            <p className="mt-3 text-splash-green/70 max-w-2xl mx-auto">A quick peek into what a Little Splash lesson actually looks like — one class at a time in a distraction-free pool.</p>
          </div>
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-splash-sand bg-black aspect-video">
            <video
              src={promoVideoUrl}
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              className="w-full h-full object-cover"
              data-testid="about-lesson-video"
            />
          </div>
        </div>
      </section>

      <Gallery />
      <CTA />
    </main>
  );
}
